import { formatarDuracao, type PainelAgentes } from '@mymaxx/shared';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  Database,
  HardDrive,
  Headset,
  LineChart,
  Server,
  Star,
  Users,
} from 'lucide-react';
import { Link } from 'react-router';
import { api, type SaudeApi } from '../lib/api';
import { relatoriosApi } from '../lib/relatorios-api';
import { supervisaoApi } from '../lib/supervisao-api';
import { useSessao } from '../lib/sessao';

const itens: { chave: keyof Omit<SaudeApi, 'status'>; rotulo: string; Icone: typeof Database }[] = [
  { chave: 'banco', rotulo: 'PostgreSQL', Icone: Database },
  { chave: 'redis', rotulo: 'Redis', Icone: Activity },
  // Já foi MinIO; desde 25/09 são arquivos em pasta na VPS. O rótulo ficou para trás e
  // aparecia na tela para a equipe inteira.
  { chave: 'storage', rotulo: 'Arquivos', Icone: HardDrive },
  { chave: 'hubsoft', rotulo: 'HubSoft', Icone: Server },
];

const HOJE = () => new Date().toISOString().slice(0, 10);

/**
 * Primeira tela depois do login.
 *
 * Quem acompanha setores cai num retrato do dia deles: o que está acontecendo agora
 * (fila, atendimentos em curso, quem está de plantão) e o que já aconteceu hoje (volume,
 * tempos, satisfação). Era a saúde da API — PostgreSQL, Redis, HubSoft — que não diz nada
 * a um supervisor e o obrigava a ir ao Monitor para descobrir se o dia estava indo bem
 * (queixa de 29/09). A saúde continua existindo, para quem administra o sistema.
 */
export function Inicio() {
  const usuario = useSessao((s) => s.usuario);
  const acompanhaSetores = !!usuario?.permissoes.includes('conversas.ver_setor');
  const administra = !!usuario?.permissoes.includes('configuracoes.gerir');
  const veRelatorios = !!usuario?.permissoes.includes('relatorios.setor');

  return (
    <div className="space-y-8">
      <section>
        <h1 className="text-2xl font-semibold">Olá, {usuario?.nome.split(' ')[0]}</h1>
        <p className="text-sm text-neutral-500">
          Perfil <strong>{usuario?.perfil.nome}</strong> ·{' '}
          {usuario?.setores.length
            ? `setores: ${usuario.setores.map((s) => `${s.nome} (${s.papel})`).join(', ')}`
            : 'sem setores atribuídos'}{' '}
          · autenticador {usuario?.totpAtivo ? 'ativo' : 'não configurado'}
        </p>
      </section>

      {acompanhaSetores && <PainelDoDia veRelatorios={veRelatorios} />}

      {/* A saúde das peças é assunto de quem administra: serve para responder "é o sistema
          ou é a minha internet?", pergunta que não é do supervisor de setor. */}
      {administra && <SaudeDaApi />}

      {administra && (
        <section className="rounded-card border border-neutral-200 p-6 dark:border-neutral-800">
          <h2 className="mb-2 text-lg font-medium">Suas permissões</h2>
          <ul className="flex flex-wrap gap-2">
            {usuario?.permissoes.map((p) => (
              <li
                key={p}
                className="rounded-full bg-marca-50 px-3 py-1 text-xs text-marca-800 dark:bg-marca-950 dark:text-marca-200"
              >
                {p}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Sem setor e sem administração não há o que resumir: resta o caminho para o
          trabalho, que é melhor do que uma tela em branco. */}
      {!acompanhaSetores && !administra && (
        <p className="text-sm text-neutral-500">
          Seu trabalho começa em{' '}
          <Link to="/atendimentos" className="underline">
            Atendimentos
          </Link>
          .
        </p>
      )}
    </div>
  );
}

/** O dia dos setores que esta pessoa acompanha: primeiro o agora, depois o acumulado. */
function PainelDoDia({ veRelatorios }: { veRelatorios: boolean }) {
  const agora = useQuery({
    queryKey: ['monitor-agentes'],
    queryFn: supervisaoApi.agentes,
    refetchInterval: 20_000,
  });
  const hoje = useQuery({
    queryKey: ['relatorio-painel', { de: HOJE(), ate: HOJE() }],
    queryFn: () => relatoriosApi.painel({ de: HOJE(), ate: HOJE() }),
    enabled: veRelatorios,
    refetchInterval: 60_000,
  });

  const setores = agora.data?.setores ?? [];
  const naFila = setores.reduce((s, st) => s + st.naFila, 0);
  const emAtendimento = setores.reduce(
    (s, st) => s + st.agentes.reduce((t, a) => t + a.ativas, 0),
    0,
  );
  const dePlantao = setores.reduce(
    (s, st) => s + st.agentes.filter((a) => a.status === 'online').length,
    0,
  );
  const emPausa = setores.reduce(
    (s, st) => s + st.agentes.filter((a) => a.status === 'pausa').length,
    0,
  );
  const i = hoje.data?.indicadores;

  return (
    <>
      <section className="space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-lg font-medium">Agora</h2>
          <Link to="/monitor" className="text-sm text-marca-700 underline dark:text-marca-300">
            Abrir o Monitor
          </Link>
        </div>

        {agora.isPending && <p className="text-sm text-neutral-500">Carregando…</p>}
        {agora.isSuccess && setores.length === 0 && (
          <p className="text-sm text-neutral-500">Você ainda não acompanha nenhum setor.</p>
        )}

        {setores.length > 0 && (
          <>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Numero
                titulo="Esperando na fila"
                valor={String(naFila)}
                rodape={naFila === 0 ? 'ninguém aguardando' : 'clientes na fila'}
                alerta={naFila > 0}
              />
              <Numero
                titulo="Em atendimento"
                valor={String(emAtendimento)}
                rodape="conversas em curso"
                icone={<Headset size={14} />}
              />
              <Numero
                titulo="De plantão"
                valor={String(dePlantao)}
                rodape={emPausa > 0 ? `${emPausa} em pausa` : 'disponíveis agora'}
                icone={<Users size={14} />}
              />
              <Numero
                titulo="Setores"
                valor={String(setores.length)}
                rodape={setores.map((s) => s.nome).join(', ')}
                pequeno
              />
            </div>

            <ul className="grid gap-2 sm:grid-cols-2">
              {setores.map((s) => (
                <LinhaSetor key={s.id} setor={s} />
              ))}
            </ul>
          </>
        )}
      </section>

      {veRelatorios && (
        <section className="space-y-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-lg font-medium">Hoje</h2>
            <Link
              to="/relatorios"
              className="flex items-center gap-1 text-sm text-marca-700 underline dark:text-marca-300"
            >
              <LineChart size={14} /> Ver relatórios
            </Link>
          </div>

          {hoje.isPending && <p className="text-sm text-neutral-500">Calculando…</p>}
          {i && (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Numero
                titulo="Atendimentos"
                valor={String(i.totalAtendimentos)}
                rodape={`${i.encerrados} encerrados`}
              />
              <Numero
                titulo="Espera média"
                valor={formatarDuracao(i.tmeSeg)}
                rodape="da fila até o atendente"
              />
              <Numero
                titulo="Duração média"
                valor={formatarDuracao(i.tmaSeg)}
                rodape={`1ª resposta em ${formatarDuracao(i.primeiraRespostaSeg)}`}
              />
              <Numero
                titulo="Satisfação"
                valor={i.csat ? `${i.csat.toFixed(1)} / 5` : '—'}
                rodape={`${i.totalAvaliacoes} avaliação(ões)`}
                icone={<Star size={14} className="text-amber-500" />}
              />
            </div>
          )}

          {hoje.data && hoje.data.porSetor.length > 0 && (
            <div className="rounded-card overflow-x-auto border border-neutral-200 p-3 dark:border-neutral-800">
              <table className="w-full text-xs">
                <thead className="text-left text-neutral-500">
                  <tr>
                    <th className="pb-1">Setor</th>
                    <th className="pb-1 text-right">Atendimentos</th>
                    <th className="pb-1 text-right">Espera</th>
                    <th className="pb-1 text-right">Duração</th>
                    <th className="pb-1 text-right">CSAT</th>
                  </tr>
                </thead>
                <tbody>
                  {hoje.data.porSetor.map((s) => (
                    <tr
                      key={s.setorId}
                      className="border-t border-neutral-100 dark:border-neutral-800"
                    >
                      <td className="py-1">{s.setor}</td>
                      <td className="py-1 text-right tabular-nums">{s.total}</td>
                      <td className="py-1 text-right">{formatarDuracao(s.tmeSeg)}</td>
                      <td className="py-1 text-right">{formatarDuracao(s.tmaSeg)}</td>
                      <td className="py-1 text-right">{s.csat?.toFixed(1) ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {hoje.data?.porSetor.length === 0 && (
            <p className="text-sm text-neutral-500">Nenhum atendimento hoje ainda.</p>
          )}
        </section>
      )}
    </>
  );
}

/** Um setor na linha do "agora": o essencial sem precisar abrir o Monitor. */
function LinhaSetor({ setor }: { setor: PainelAgentes['setores'][number] }) {
  const emAtendimento = setor.agentes.reduce((t, a) => t + a.ativas, 0);
  const online = setor.agentes.filter((a) => a.status === 'online').length;

  return (
    <li className="rounded-card flex flex-wrap items-center gap-x-4 gap-y-1 border border-neutral-200 px-3 py-2 text-sm dark:border-neutral-800">
      <span className="font-medium">{setor.nome}</span>
      <span
        className={setor.naFila > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-neutral-500'}
      >
        {setor.naFila > 0 ? `${setor.naFila} na fila` : 'fila vazia'}
      </span>
      <span className="text-neutral-500">{emAtendimento} em atendimento</span>
      <span className="text-neutral-500">{online} de plantão</span>
      {!setor.dentroDoHorario && <span className="text-neutral-400">fora do horário</span>}
      {!setor.distribuicaoAutomatica && (
        <span className="text-neutral-400">fila automática desligada</span>
      )}
    </li>
  );
}

function Numero({
  titulo,
  valor,
  rodape,
  icone,
  alerta,
  pequeno,
}: {
  titulo: string;
  valor: string;
  rodape?: string;
  icone?: React.ReactNode;
  alerta?: boolean;
  pequeno?: boolean;
}) {
  return (
    <div
      className={`rounded-card border p-3 ${
        alerta
          ? 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40'
          : 'border-neutral-200 dark:border-neutral-800'
      }`}
    >
      <p className="flex items-center gap-1 text-xs text-neutral-500">
        {icone} {titulo}
      </p>
      <p className={`mt-1 font-semibold ${pequeno ? 'text-lg' : 'text-2xl'}`}>{valor}</p>
      {rodape && <p className="truncate text-[11px] text-neutral-500">{rodape}</p>}
    </div>
  );
}

/** As peças do sistema de pé — responde "é o sistema ou é a minha internet?". */
function SaudeDaApi() {
  const saude = useQuery({
    queryKey: ['health'],
    queryFn: () => api.get<SaudeApi>('/v1/health', { autenticado: false }),
    refetchInterval: 15_000,
  });

  return (
    <section className="rounded-card border border-neutral-200 p-6 dark:border-neutral-800">
      <h2 className="mb-4 text-lg font-medium">Saúde da API</h2>
      {saude.isPending && <p className="text-neutral-500">Consultando…</p>}
      {saude.isError && <p className="text-red-600">API indisponível: {saude.error.message}</p>}
      {saude.data && (
        <ul className="grid gap-3 sm:grid-cols-2">
          {itens.map(({ chave, rotulo, Icone }) => {
            const v = saude.data[chave];
            return (
              <li
                key={chave}
                className="flex items-center gap-3 rounded-lg bg-neutral-50 p-3 dark:bg-neutral-900"
              >
                <Icone className={v.ok ? 'text-green-600' : 'text-red-600'} size={20} />
                <div className="flex-1">
                  <p className="font-medium">{rotulo}</p>
                  <p className="text-xs text-neutral-500">
                    {v.ok ? `${v.ms} ms` : (v.erro ?? 'falha')}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
